import CaseStudy from '../components/CaseStudy';

const data = {
  company: 'Maqsad · Edtech · Pakistan',
  title: 'Maqsad Sikkay:<br/>Designing for<br/>Retention',
  subtitle: 'A virtual currency system that turned passive users into engaged ones - and rewired how students thought about paying inside the app.',
  metaRole: 'Feature Design, Engagement Mechanics',
  metaPlatform: 'iOS & Android Mobile App',
  metaExtraLabel: 'Company Scale',
  metaExtra: '0 → 4M Users · PKR 12M ARR',
  outcomeValue: '2× sessions',
  outcomeSub: '25% resubscription with real money',
  heroPlaceholder: 'Hero image: Composed mockup of the Sikkay coins UI - the balance display, earning moment, or missions screen on phone mockups',
  heroImage: '/gamification-intro.png',
  metaItems: [
    { label: 'My Role', value: 'Feature Design, Engagement Mechanics' },
    { label: 'Platform', value: 'iOS & Android Mobile App' },
    { label: 'Collaborators', value: 'Product, Engineering, Community' },
    { label: 'Key Outcome', value: '2× app sessions · 25% resubscription' },
  ],
  sections: [
    {
      type: 'text',
      label: 'Overview',
      heading: 'Strong engagement. But no reason to stay.',
      paragraphs: [
        'Maqsad had built a genuinely useful product - students were coming in, studying, and leaving. But we were missing the layer that makes an app feel like somewhere you <em>belong</em>. No reward for exploration, no recognition of consistent behaviour, no bridge between using the app and paying within it.',
        'Maqsad Sikkay - a virtual coin system - was designed to close that gap across three dimensions: engagement, habit formation, and payment behaviour.',
      ],
    },
    {
      type: 'text',
      label: 'Problem',
      heading: 'Utility without stickiness isn\'t a product. It\'s a tool.',
      paragraphs: [
        'Users weren\'t completing profile setups, limiting personalisation. Our DoubtSolve feature wasn\'t being discovered. And most critically, <strong>users had no experience paying within the app</strong> - they defaulted to manual offline payments, meaning more friction and less revenue certainty.',
      ],
      hmw: 'How might we reward meaningful user behaviour while building the payment familiarity that drives long-term monetisation?',
    },
    {
      type: 'text',
      label: 'Solution',
      heading: 'Coins that felt earned, not given.',
      paragraphs: [
        'The challenge with any reward system is making the currency feel meaningful. Sikkay had to feel like something worth having - not a gimmick layered on top of a real product.',
        'We focused the first release on three things: <strong>teasing curiosity</strong> through contextual nudges before users fully understood the system; <strong>rewarding high-value actions</strong> - profile completion, first DoubtSolve, referrals - so the first earning moment came quickly; and <strong>building a clear value exchange</strong> through an informational page that made spending Sikkay feel worthwhile.',
      ],
    },
    {
      type: 'image',
      size: 'medium',
      src: '/gamification-launch.png',
      text: 'Screens: The Sikkay information page + in-app banner introducing the feature - show the curiosity-building intro moment',
      caption: 'Introducing Sikkay - building curiosity before explaining the system',
    },
    {
      type: 'text',
      label: 'The Experiment',
      heading: 'Teaching payment behaviour through a "free" win.',
      paragraphs: [
        'The most strategically interesting part of Sikkay was a deliberate experiment in payment behaviour design. We awarded users who completed their free DoubtSolve credits with enough Sikkay to "purchase" a one-month subscription entirely within the app.',
      ],
      highlight: 'Users felt they had <strong>gamed the system</strong> - getting a month of premium for free. What they were actually doing was completing their <strong>first in-app payment flow</strong>. The emotional cost was zero, so the friction disappeared. They\'d been through the flow. It felt familiar. The barrier was gone.',
    },
    {
      type: 'image',
      size: 'tall',
      src: '/gamification-payment.png',
      text: 'Screen sequence: Full Sikkay payment journey - payment method screen → instructions → Sikkay discount applied → success state',
      caption: 'The in-app payment journey - completing a "free" purchase that trained real payment behaviour',
    },
    {
      type: 'image',
      size: 'medium',
      src: '/gamification-referral.png',
      text: 'Screens: Gamification missions screen + referral screen - how users earn Sikkay through different actions',
      caption: 'Earning Sikkay through referrals and missions',
    },
    {
      type: 'text',
      label: 'Impact',
      heading: 'Engagement, retention, and revenue - all moved.',
      paragraphs: [],
      impact: [
        { num: '2×', label: 'Spike in app sessions post-launch' },
        { num: '25%', label: 'Resubscription rate from Sikkay-exposed users' },
        { num: '29%', label: 'Profile completion rate when nudged with reward' },
      ],
      afterImpact: [
        'Daily DoubtSolve sales stabilised at ~15/day. 50% of users explored the Sikkay information page. The feature became the foundation for Maqsad\'s broader loyalty and monetisation roadmap.',
      ],
    },
    {
      type: 'text',
      label: 'Learnings',
      heading: 'Rewards are most powerful when they serve the business model.',
      paragraphs: [
        'The lesson from Sikkay: <strong>use incentives strategically to change behaviour at the right moment</strong>. The "free win" experiment worked because it removed emotional friction from a high-stakes action (paying) at exactly the right point in a user\'s journey.',
        'Gamification that\'s purely cosmetic creates noise. Gamification connected to real business outcomes - payment familiarity, feature adoption, retention - creates compounding value.',
      ],
    },
  ],
  nextTo: '/the-brief',
  nextLabel: 'Next Project',
  nextTitle: 'The Brief: AI-Powered Case Study Writer',
};

export default function Gamification() {
  return <CaseStudy data={data} />;
}
